import { useState, useEffect, useRef } from 'react';
import { User, Users, Shield, MessageSquare, LogOut, Bell, Clock } from 'lucide-react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { NhostProvider, useAuthenticationStatus, useUserData } from '@nhost/react';
import nhost from './nhost';
import Auth from './components/Auth';
import Wines from './components/Wines';
import Sessions from './components/Sessions';
import Calendar from './components/Calendar';
import Statistics from './components/Statistics';
import Admin from './components/Admin';
import Profile from './components/Profile';
import FeedbackModal from './components/FeedbackModal';
import MemberProfile from './components/MemberProfile';
import MembersModal from './components/MembersModal';
import WorldMap from './components/WorldMap';
import Grapes from './components/Grapes';
import NewWinePopup from './components/NewWinePopup';
import { pushBack, popBack, swapBack } from './historyStack';

// ISO 3166-1 alpha-2 koder → används med flagcdn.com för att visa flaggbilder
const COUNTRY_FLAGS = {
  'Frankrike': 'fr', 'Italien': 'it', 'Spanien': 'es', 'Portugal': 'pt',
  'Tyskland': 'de', 'Österrike': 'at', 'Schweiz': 'ch', 'Ungern': 'hu',
  'Grekland': 'gr', 'Slovenien': 'si', 'Kroatien': 'hr', 'Rumänien': 'ro',
  'Bulgarien': 'bg', 'Georgien': 'ge', 'Moldavien': 'md', 'Armenien': 'am',
  'USA': 'us', 'Kanada': 'ca', 'Argentina': 'ar', 'Chile': 'cl',
  'Uruguay': 'uy', 'Brasilien': 'br', 'Australien': 'au', 'Nya Zeeland': 'nz',
  'Sydafrika': 'za', 'Marocko': 'ma', 'Israel': 'il', 'Libanon': 'lb',
  'Kina': 'cn', 'Japan': 'jp', 'Turkiet': 'tr', 'Cypern': 'cy',
  'Luxemburg': 'lu', 'Makedonien': 'mk', 'Albanien': 'al', 'Serbien': 'rs',
  'Montenegro': 'me', 'Bosnien och Hercegovina': 'ba',
};

// Global cache – sparar data i 60 sekunder
const CACHE_TTL = 60 * 1000;
const cache = {};

function getCached(key) {
  const entry = cache[key];
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) { delete cache[key]; return null; }
  return entry.data;
}

function setCache(key, data) {
  cache[key] = { data, ts: Date.now() };
}

function invalidateCache(key) {
  delete cache[key];
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuthenticationStatus();
  const user = useUserData();
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState(() => localStorage.getItem('activeTab') || 'karta');
  const [mapSearch, setMapSearch] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [viewingMember, setViewingMember] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [mapCountry, setMapCountry] = useState(null);
  const [mapWineIds, setMapWineIds] = useState(null);
  const [openWineId, setOpenWineId] = useState(null);
  const [openGrapeName, setOpenGrapeName] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);
  const [adminMessage, setAdminMessage] = useState(null);
  const pendingAdminMsgRef = useRef(null);
  const [newWineId, setNewWineId] = useState(null);
  const pendingNewWineIdRef = useRef(null);

  function changeTab(t) {
    const prev = tab;
    setTab(t);
    localStorage.setItem('activeTab', t);
    pushBack(() => {
      setTab(prev);
      localStorage.setItem('activeTab', prev);
    });
  }

  function onWineClick(wine) {
    setShowProfile(false);
    setViewingMember(null);
    setOpenWineId(wine.id);
    changeTab('viner');
  }

  function onGrapeClick(grapeName) {
    setShowProfile(false);
    setViewingMember(null);
    setOpenGrapeName(grapeName);
    changeTab('druvor');
  }

  function openMember(id) { pushBack(() => setViewingMember(null)); setViewingMember(id); }
  function closeMember() { popBack(); setViewingMember(null); }
  function swapMember(id) { swapBack(() => setViewingMember(null)); setShowMembers(false); setViewingMember(id); }

  function openProfile() { pushBack(() => setShowProfile(false)); setShowProfile(true); }
  function closeProfile() { popBack(); setShowProfile(false); fetchProfile(); }

  function openAdmin() { pushBack(() => setShowAdmin(false)); setShowAdmin(true); }
  function closeAdmin() { popBack(); setShowAdmin(false); fetchProfile(); }

  function openMembers() { pushBack(() => setShowMembers(false)); setShowMembers(true); }
  function closeMembers() { popBack(); setShowMembers(false); }

  function openFeedback() { pushBack(() => setShowFeedback(false)); setShowFeedback(true); }
  function closeFeedback() { popBack(); setShowFeedback(false); }

  function openDrawer() { pushBack(() => setShowDrawer(false)); setShowDrawer(true); }
  function closeDrawer() { popBack(); setShowDrawer(false); }

  useEffect(() => {
    if (isAuthenticated && user) fetchProfile();
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!profile?.id) return;
    const since = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    nhost.graphql.request(`
      query GetOnlineCount($since: timestamptz!) {
        profiles_aggregate(where: { approved: { _eq: true }, last_seen: { _gte: $since } }) {
          aggregate { count }
        }
      }
    `, { since }).then(({ data }) => {
      setOnlineCount(data?.profiles_aggregate?.aggregate?.count || 0);
    });
  }, [profile?.id]);

  useEffect(() => {
    const urlMsg = new URLSearchParams(window.location.search).get('admin_msg');
    if (urlMsg) {
      window.history.replaceState({}, '', window.location.pathname);
      pendingAdminMsgRef.current = urlMsg;
    }
    const swPending = window.__pendingAdminMsg;
    if (swPending) {
      window.__pendingAdminMsg = null;
      pendingAdminMsgRef.current = swPending;
    }

    const urlWineId = new URLSearchParams(window.location.search).get('new_wine');
    if (urlWineId) {
      window.history.replaceState({}, '', window.location.pathname);
      pendingNewWineIdRef.current = urlWineId;
    }
    const swPendingWineId = window.__pendingNewWineId;
    if (swPendingWineId) {
      window.__pendingNewWineId = null;
      pendingNewWineIdRef.current = swPendingWineId;
    }

    function onSwMessage(event) {
      if (event.data?.data?.type === 'admin_message') {
        setAdminMessage(event.data.data.message);
      }
      if (event.data?.data?.type === 'new_wine' && event.data.data.wineId) {
        setNewWineId(event.data.data.wineId);
      }
    }
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onSwMessage);
  }, []);

  useEffect(() => {
    if (profile && pendingAdminMsgRef.current) {
      const msg = pendingAdminMsgRef.current;
      pendingAdminMsgRef.current = null;
      setTimeout(() => setAdminMessage(msg), 300);
    }
    if (profile && pendingNewWineIdRef.current) {
      const id = pendingNewWineIdRef.current;
      pendingNewWineIdRef.current = null;
      setTimeout(() => setNewWineId(id), 300);
    }
  }, [profile]);

  async function updateLastSeen(userId) {
    const now = new Date().toISOString();
    await nhost.graphql.request(`
      mutation UpdateLastSeen($id: uuid!, $now: timestamptz!) {
        update_profiles_by_pk(pk_columns: { id: $id }, _set: { last_seen: $now }) { id }
      }
    `, { id: userId, now });
  }

  async function fetchProfile() {
    const { data } = await nhost.graphql.request(`
      query { profiles_by_pk(id: "${user.id}") { id display_name approved is_admin created_at avatar_url calendar_token } }
    `);
    if (data?.profiles_by_pk) {
      setProfile(data.profiles_by_pk);
      updateLastSeen(data.profiles_by_pk.id);
      clearInterval(window._lastSeenInterval);
      window._lastSeenInterval = setInterval(() => updateLastSeen(data.profiles_by_pk.id), 5 * 60 * 1000);
    } else {
      const displayName = user.displayName || user.email;
      await nhost.graphql.request(`
        mutation {
          insert_profiles_one(object: { id: "${user.id}", display_name: "${displayName}", approved: false, is_admin: false },
            on_conflict: { constraint: profiles_pkey, update_columns: [] }) { id }
        }
      `);
      fetch('/api/push?action=notify-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName }),
      }).catch(() => {});
      fetchProfile();
    }
  }

  if (isLoading) return <div className="loading">Vinligan</div>;
  if (!isAuthenticated) return <Auth />;
  if (!profile) return <div className="loading">Laddar...</div>;

  if (!profile.approved) {
    return (
      <div className="pending-screen">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <Clock size={40} color="var(--wine)" strokeWidth={1.4} />
        </div>
        <div className="pending-title">Väntar på godkännande</div>
        <p className="pending-text">Din ansökan väntar på godkännande av admin. Du får tillgång så snart den är godkänd.</p>
        <button className="btn-secondary" style={{ marginTop: 24, maxWidth: 200 }} onClick={() => nhost.auth.signOut()}>Logga ut</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', position: 'relative', minHeight: '100vh' }}>

      {/* Topbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '12px 16px 8px', position: 'relative',
        zIndex: 10, background: 'var(--cream)',
      }}>
        {/* Hamburger-knapp */}
        <button
          onClick={() => openDrawer()}
          style={{
            position: 'absolute', left: 16,
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 4, display: 'flex', flexDirection: 'column',
            gap: 5, alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36,
          }}
        >
          <span style={{ display: 'block', width: 20, height: 2, background: 'var(--wine)', borderRadius: 2 }} />
          <span style={{ display: 'block', width: 20, height: 2, background: 'var(--wine)', borderRadius: 2 }} />
          <span style={{ display: 'block', width: 20, height: 2, background: 'var(--wine)', borderRadius: 2 }} />
        </button>

        <button
          onClick={() => setRefreshKey(k => k + 1)}
          style={{ textAlign: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <div className="topbar-logo">Vinligan</div>
          <div className="topbar-anno">Anno 1985</div>
        </button>
      </div>

      {/* Drawer backdrop */}
      {showDrawer && (
        <div
          onClick={closeDrawer}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(26,10,15,.45)',
            zIndex: 200,
          }}
        />
      )}

      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: 290,
        background: 'var(--cream)',
        zIndex: 201,
        transform: showDrawer ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: showDrawer ? '8px 0 32px rgba(60,20,30,.18)' : 'none',
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
      }}>
        {/* Logotype */}
        <div style={{ padding: '52px 20px 16px' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 600, color: 'var(--wine)', letterSpacing: 0.5 }}>Vinligan</div>
          <div style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: 2.5, textTransform: 'uppercase', marginTop: 1 }}>Anno 1985</div>
        </div>

        {/* Profilrad */}
        <button
          onClick={() => { closeDrawer(); openProfile(); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px', margin: '0 12px 16px',
            background: 'var(--wine-light)', border: 'none', cursor: 'pointer',
            borderRadius: 14, textAlign: 'left', fontFamily: 'inherit',
          }}
        >
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: 'var(--wine)', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontFamily: 'var(--font-display)', fontWeight: 600,
            overflow: 'hidden', flexShrink: 0,
          }}>
            {profile.avatar_url
              ? <img src={profile.avatar_url} alt={profile.display_name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : profile.display_name?.[0]?.toUpperCase()
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--wine)', fontFamily: 'var(--font-display)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {profile.display_name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--wine)', opacity: 0.65, marginTop: 2 }}>Visa min profil ›</div>
          </div>
        </button>

        {/* Meny-items */}
        <div style={{ flex: 1 }}>
          {[
            { Icon: User,          label: 'Min profil',         action: () => { closeDrawer(); openProfile(); } },
            { Icon: Users,         label: 'Medlemmar',          action: () => { closeDrawer(); setTimeout(() => openMembers(), 0); }, badge: onlineCount > 0 ? `${onlineCount} online` : null },
            ...(profile.is_admin ? [{ Icon: Shield, label: 'Admin', action: () => { closeDrawer(); openAdmin(); } }] : []),
            { Icon: MessageSquare, label: 'Feedback & förslag', action: () => { closeDrawer(); openFeedback(); } },
          ].map(({ Icon, label, action, badge }) => (
            <button
              key={label}
              onClick={action}
              style={{
                width: '100%', textAlign: 'left',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '13px 20px',
                display: 'flex', alignItems: 'center', gap: 14,
                fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit',
              }}
            >
              <Icon size={18} strokeWidth={1.7} color="var(--wine)" />
              <span style={{ flex: 1 }}>{label}</span>
              {badge && (
                <span style={{ fontSize: 11, color: '#1F8A5B', background: '#E1F5EE', borderRadius: 20, padding: '3px 8px', fontWeight: 500 }}>
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Logga ut */}
        <div style={{ padding: '12px 0 32px', borderTop: '1px solid rgba(60,20,30,.07)' }}>
          <button
            onClick={() => nhost.auth.signOut()}
            style={{
              width: '100%', textAlign: 'left',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '13px 20px',
              display: 'flex', alignItems: 'center', gap: 14,
              fontSize: 15, color: 'var(--muted)', fontFamily: 'inherit',
            }}
          >
            <LogOut size={18} strokeWidth={1.7} color="var(--muted)" />
            Logga ut
          </button>
        </div>
      </div>

      {/* Flikar – skicka cache-hjälpare som props */}
      {tab === 'karta' && (
        <WorldMap
          onNavigate={changeTab}
          onSetSearch={s => { setMapSearch(s); }}
          onCountrySelect={data => { pushBack(() => { setMapCountry(null); setMapWineIds(null); }); setMapCountry(data); setMapWineIds(null); }}
        />
      )}

      {tab === 'druvor' && <Grapes key={refreshKey} initialOpenGrapeName={openGrapeName} />}

      {tab === 'viner' && (
        <Wines
          key={`${refreshKey}-${mapSearch}-${mapWineIds ? mapWineIds.join(',') : ''}`}
          profile={profile}
          initialSearch={mapSearch}
          initialWineIds={mapWineIds}
          initialOpenWineId={openWineId}
          onClearMapSearch={() => { setMapSearch(''); setMapWineIds(null); }}
          onViewMember={openMember}
          getCached={getCached}
          setCache={setCache}
          invalidateCache={invalidateCache}
        />
      )}
      {tab === 'provningar' && (
        <Sessions
          key={refreshKey}
          profile={profile}
          onViewMember={openMember}
          getCached={getCached}
          setCache={setCache}
          invalidateCache={invalidateCache}
        />
      )}
      {tab === 'kalender' && (
        <Calendar
          key={refreshKey}
          profile={profile}
          onViewMember={openMember}
          getCached={getCached}
          setCache={setCache}
          invalidateCache={invalidateCache}
        />
      )}
      {tab === 'statistik' && <Statistics key={refreshKey} onWineClick={onWineClick} onViewMember={openMember} />}

      <nav className="bottom-nav">
        {[
          { id: 'karta', label: 'Karta', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z"/><path d="M9 4v14"/><path d="M15 6v14"/></svg> },
          { id: 'viner', label: 'Viner', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M8 22h8"/><path d="M12 15v7"/><path d="M7 3h10s-.5 5-1 6.5A4.5 4.5 0 0 1 12 13a4.5 4.5 0 0 1-4-3.5C7.5 8 7 3 7 3Z"/></svg> },
          { id: 'druvor', label: 'Druvor', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="8.5" cy="11" r="2.5"/><circle cx="15.5" cy="11" r="2.5"/><circle cx="12" cy="16.5" r="2.5"/><circle cx="12" cy="5.5" r="2.5"/><path d="M12 3V1.5"/><path d="M13.5 1.5h2"/></svg> },
          { id: 'provningar', label: 'Provningar', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="17" rx="2.5"/><path d="M3 9.5h18"/><path d="M8 2.5v4"/><path d="M16 2.5v4"/></svg> },
          { id: 'kalender', label: 'Kalender', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="17" rx="2.5"/><path d="M3 9.5h18"/><path d="M8 2.5v4"/><path d="M16 2.5v4"/><circle cx="12" cy="15.5" r="1.5" fill="currentColor" stroke="none"/></svg> },
          { id: 'statistik', label: 'Statistik', svg: <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21V11"/><path d="M12 21V4"/><path d="M19 21v-6"/></svg> },
        ].map(t => (
          <button key={t.id} className={`nav-btn ${tab === t.id ? 'active' : ''}`} onClick={() => changeTab(t.id)}>
            <span className="nav-icon">{t.svg}</span>
            {t.label}
          </button>
        ))}
      </nav>

      {/* Kart-landspanel – höger drawer */}
      {mapCountry && (
        <div onClick={() => { popBack(); setMapCountry(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200 }} />
      )}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 260,
        background: '#f7f0eb',
        zIndex: 201,
        transform: mapCountry ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: mapCountry ? '-4px 0 24px rgba(0,0,0,0.18)' : 'none',
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
      }}>
        {mapCountry && (
          <>
            <div style={{ padding: '48px 16px 14px', borderBottom: '0.5px solid var(--border)', background: 'var(--wine-light)', flexShrink: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 500, color: 'var(--wine)', display: 'flex', alignItems: 'center', gap: 8 }}>
                {COUNTRY_FLAGS[mapCountry.name] && (
                  <img
                    src={`https://flagcdn.com/w40/${COUNTRY_FLAGS[mapCountry.name]}.png`}
                    alt={mapCountry.name}
                    style={{ width: 24, height: 'auto', borderRadius: 2, flexShrink: 0 }}
                  />
                )}
                {mapCountry.name}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{mapCountry.total} vin{mapCountry.total !== 1 ? 'er' : ''}</div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 16px' }}>
              <button
                onClick={() => { setMapSearch(''); setMapWineIds(mapCountry.wineIds); changeTab('viner'); setMapCountry(null); }}
                style={{ width: '100%', marginBottom: 14, fontSize: 13, color: 'var(--wine)', background: 'var(--wine-light)', border: '0.5px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, textAlign: 'left' }}
              >
                Visa alla viner från {mapCountry.name} →
              </button>
              {mapCountry.regions.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Regioner</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {mapCountry.regions.map(([region, count]) => {
                      const maxR = Math.max(...mapCountry.regions.map(([,c]) => c), 1);
                      const t = Math.log(count + 1) / Math.log(maxR + 1);
                      return (
                        <button
                          key={region}
                          onClick={() => { setMapSearch(region === 'Okänd region' ? '' : region); setMapWineIds(region === 'Okänd region' ? mapCountry.wineIds : null); changeTab('viner'); setMapCountry(null); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: '0.5px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                        >
                          <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: `rgb(${Math.round(245-(245-92)*t)},${Math.round(232-(232-26)*t)},${Math.round(235-(235-40)*t)})` }} />
                          <div style={{ flex: 1, fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>{region}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{count}st</div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {showAdmin && <Admin profile={profile} onClose={closeAdmin} />}
      {showProfile && <Profile profile={profile} onClose={closeProfile} onNavigate={changeTab} onWineClick={onWineClick} onGrapeClick={onGrapeClick} />}
      {showFeedback && <FeedbackModal profile={profile} onClose={closeFeedback} />}
      {viewingMember && <MemberProfile userId={viewingMember} onClose={closeMember} onWineClick={onWineClick} onGrapeClick={onGrapeClick} />}
      {showMembers && <MembersModal profile={profile} onClose={closeMembers} onViewMember={swapMember} />}

      {adminMessage && (
        <div className="modal-bg" onClick={() => setAdminMessage(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Bell size={20} color="var(--wine)" strokeWidth={1.7} />
              <div className="modal-title" style={{ marginBottom: 0 }}>Meddelande från administratören</div>
            </div>
            <div style={{ fontSize: 15, color: 'var(--ink)', lineHeight: 1.6, padding: '12px 0', whiteSpace: 'pre-wrap' }}>{adminMessage}</div>
            <button className="btn-primary" style={{ marginTop: 8 }} onClick={() => setAdminMessage(null)}>Stäng</button>
          </div>
        </div>
      )}

      {newWineId && (
        <NewWinePopup wineId={newWineId} onClose={() => setNewWineId(null)} onWineClick={onWineClick} />
      )}

      <Analytics />
      <SpeedInsights />
    </div>
  );
}

export default function App() {
  return (
    <NhostProvider nhost={nhost}>
      <AppContent />
    </NhostProvider>
  );
}
